import type {
  AttachmentTemplateDto,
  SpecificationTemplateDealerOptionDto,
  SpecificationTemplateItemDto,
  SpecificationTemplateServiceContractDto,
  SpecificationTemplateTransportOptionDto,
  SpecificationTemplateWarrantyDto
} from '@api/machines'
import { GENERAL_MACHINE_TYPES, getDealerFitName } from '@common/utils'
import {
  MolsonArchived,
  type MolsonArchivedProps,
  MolsonBox,
  MolsonCheckbox,
  MolsonTypography,
  type MolsonTypographyProps
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'

type MasterDetailProps = {
  addedDealerOptions: SpecificationTemplateDealerOptionDto[]
  addedFactoryFit: SpecificationTemplateItemDto[]
  addedServiceContract: SpecificationTemplateServiceContractDto
  addedTransportOption: SpecificationTemplateTransportOptionDto
  addedWarranty: SpecificationTemplateWarrantyDto
  itemType: string
  machineAttachments: AttachmentTemplateDto[]
  keyPrefix: string
  isServiceContractEmpty?: boolean
}

type SectionSubtitleProps = {
  label: string
  isSubtitle?: boolean
} & MolsonTypographyProps

export const SectionSubtitle = ({
  label,
  isSubtitle,
  ...props
}: SectionSubtitleProps) => {
  const theme = molsonUseTheme()
  return (
    <MolsonTypography
      {...props}
      sx={{
        color: theme.palette.secondary[900],
        fontWeight: '700',
        lineHeight: '24px',
        fontSize: isSubtitle ? '1rem' : '1.25rem'
      }}
    >
      {label}
    </MolsonTypography>
  )
}

const CustomArchived = (props: MolsonArchivedProps) => (
  <MolsonArchived {...props} outerBoxSx={{ maxHeight: 62 }} />
)

export const MasterDetail = ({
  addedDealerOptions,
  addedFactoryFit,
  addedServiceContract,
  addedTransportOption,
  addedWarranty,
  isServiceContractEmpty,
  itemType,
  machineAttachments,
  keyPrefix
}: MasterDetailProps) => {
  const { t } = useTranslation('', { keyPrefix })

  const displayEqOptions =
    addedFactoryFit?.length > 0 || addedDealerOptions?.length > 0

  const displayServices =
    addedWarranty ||
    addedServiceContract ||
    addedTransportOption ||
    !isServiceContractEmpty

  const displayAttachmentsWithinMachine =
    itemType === GENERAL_MACHINE_TYPES.MACHINE && machineAttachments.length > 0

  const standardFactoryFits = addedFactoryFit.filter(
    item => (item?.id ?? 0) > 0
  )
  const customFactoryFits = addedFactoryFit.filter(item => (item?.id ?? 0) < 0)

  const standardDealerFits = addedDealerOptions.filter(
    item => (item?.id ?? 0) > 0
  )
  const customDealerFits = addedDealerOptions.filter(
    item => (item?.id ?? 0) < 0
  )

  const getAddedServiceContract = (
    addedServiceContract: SpecificationTemplateServiceContractDto
  ) => {
    if (addedServiceContract?.isArchived) {
      return (
        <CustomArchived
          label={`${t('serviceContract')} (${
            addedServiceContract?.hours
          } ${t('hrs')})`}
          checked={true}
          disabled={true}
        />
      )
    }
    return (
      <MolsonBox flex={1}>
        <MolsonCheckbox
          label={`${t('serviceContract')} (${
            addedServiceContract?.hours
          } ${t('hrs')})`}
          checked={true}
          disabled={true}
        />
      </MolsonBox>
    )
  }

  const getAddedTransportOption = (
    addedTransportOption: SpecificationTemplateTransportOptionDto
  ) => {
    if (!addedTransportOption) {
      return null
    }
    if (addedTransportOption?.isArchived) {
      return (
        <CustomArchived
          checked={true}
          disabled={true}
          label={`${t('transport')} (${addedTransportOption?.name})`}
        />
      )
    }
    return (
      <MolsonBox flex={1}>
        <MolsonCheckbox
          checked={true}
          disabled={true}
          label={`${t('transport')} (${addedTransportOption?.name})`}
        />
      </MolsonBox>
    )
  }

  return (
    <MolsonBox pb={3} pt={0.5} px={1}>
      {displayEqOptions && (
        <MolsonTypography variant="h3" mb={3}>
          {t('equipmentOptions')}
        </MolsonTypography>
      )}

      {addedFactoryFit.length > 0 && (
        <MolsonBox display="flex" gap={2} flexDirection="column">
          <SectionSubtitle label={t('factoryFit')} />
          {standardFactoryFits?.length > 0 && (
            <MolsonBox display="flex" flexWrap="wrap" gap={2}>
              {standardFactoryFits?.map(factoryFit => (
                <MolsonBox flex={1} key={factoryFit.id}>
                  {factoryFit?.isArchived ? (
                    <CustomArchived
                      checked={true}
                      disabled={true}
                      key={factoryFit.id}
                      label={`${factoryFit.itemName}`}
                      outerBoxSx={{ maxHeight: 62 }}
                    />
                  ) : (
                    <MolsonCheckbox
                      checked={true}
                      disabled={true}
                      label={factoryFit.itemName ?? undefined}
                    />
                  )}
                </MolsonBox>
              ))}
            </MolsonBox>
          )}

          {customFactoryFits?.length > 0 && (
            <>
              <SectionSubtitle label={t('customItems')} isSubtitle={true} />
              <MolsonBox display="flex" flexWrap="wrap" gap={2}>
                {customFactoryFits?.map(factoryFit => (
                  <MolsonBox flex={1} key={factoryFit.id}>
                    {factoryFit?.isArchived ? (
                      <CustomArchived
                        checked={true}
                        disabled={true}
                        key={factoryFit.id}
                        label={`${factoryFit.itemName}`}
                        outerBoxSx={{ maxHeight: 62 }}
                      />
                    ) : (
                      <MolsonCheckbox
                        checked={true}
                        disabled={true}
                        label={factoryFit.itemName ?? undefined}
                      />
                    )}
                  </MolsonBox>
                ))}
              </MolsonBox>
            </>
          )}
        </MolsonBox>
      )}

      {addedDealerOptions.length > 0 && (
        <MolsonBox display="flex" gap={2} flexDirection="column" mt={3}>
          <SectionSubtitle label={t('dealerFit')} />
          {standardDealerFits?.length > 0 && (
            <MolsonBox display="flex" flexWrap="wrap" gap={2}>
              {standardDealerFits?.map(option => {
                if (option?.isArchived) {
                  return (
                    <MolsonBox flex={1} key={option?.id}>
                      <CustomArchived
                        checked={true}
                        disabled={true}
                        label={getDealerFitName(option) ?? ''}
                      />
                    </MolsonBox>
                  )
                }
                return (
                  <MolsonBox flex={1} key={option?.id}>
                    <MolsonCheckbox
                      checked={true}
                      disabled={true}
                      label={getDealerFitName(option) ?? ''}
                    />
                  </MolsonBox>
                )
              })}
            </MolsonBox>
          )}

          {customDealerFits?.length > 0 && (
            <>
              <SectionSubtitle label={t('customItems')} isSubtitle={true} />
              <MolsonBox display="flex" flexWrap="wrap" gap={2}>
                {customDealerFits?.map(factoryFit => (
                  <MolsonBox flex={1} key={factoryFit.id}>
                    {factoryFit?.isArchived ? (
                      <CustomArchived
                        checked={true}
                        disabled={true}
                        key={factoryFit.id}
                        label={`${factoryFit.itemName}`}
                        outerBoxSx={{ maxHeight: 62 }}
                      />
                    ) : (
                      <MolsonCheckbox
                        checked={true}
                        disabled={true}
                        label={factoryFit.itemName ?? undefined}
                      />
                    )}
                  </MolsonBox>
                ))}
              </MolsonBox>
            </>
          )}
        </MolsonBox>
      )}

      {displayServices && (
        <MolsonTypography mb={3} mt={displayEqOptions ? 5 : 0} variant="h3">
          {t('services')}
        </MolsonTypography>
      )}

      {displayServices && (
        <MolsonBox display="flex" flexWrap="wrap" gap={2}>
          {addedWarranty &&
            (addedWarranty?.isArchived ? (
              <CustomArchived
                label={`${addedWarranty?.cover} ${t('warranty')} (${
                  addedWarranty?.hours
                } ${t('hrs')} / ${addedWarranty?.months} ${t('monthsLowerCase')})`}
                checked={true}
                disabled={true}
              />
            ) : (
              <MolsonBox flex={1}>
                <MolsonCheckbox
                  label={`${addedWarranty?.cover} ${t('warranty')} (${
                    addedWarranty?.hours
                  } ${t('hrs')} / ${addedWarranty?.months} ${t('monthsLowerCase')})`}
                  checked={true}
                  disabled={true}
                />
              </MolsonBox>
            ))}

          {addedServiceContract
            ? getAddedServiceContract(addedServiceContract)
            : !isServiceContractEmpty && (
                <MolsonBox flex={1}>
                  <MolsonCheckbox
                    checked={true}
                    disabled={true}
                    label={`${t('serviceContractNone')}`}
                  />
                </MolsonBox>
              )}

          {getAddedTransportOption(addedTransportOption)}
        </MolsonBox>
      )}

      {displayAttachmentsWithinMachine && (
        <>
          <MolsonTypography
            mb={3}
            mt={displayEqOptions || displayServices ? 5 : 0}
            variant="h3"
          >
            {t('attachments')}
          </MolsonTypography>
          <MolsonBox display="flex" flexWrap="wrap" gap={2}>
            {machineAttachments.map(attachment => {
              return (
                <MolsonBox flex={1} key={attachment.id}>
                  {attachment?.isArchived ? (
                    <CustomArchived
                      label={`${attachment.brand} ${attachment.itemName}`}
                      checked={true}
                      disabled={true}
                    />
                  ) : (
                    <MolsonCheckbox
                      label={`${attachment.brand} ${attachment.itemName}`}
                      checked={true}
                      disabled={true}
                    />
                  )}
                </MolsonBox>
              )
            })}
          </MolsonBox>
        </>
      )}

      {!(
        displayEqOptions ||
        displayServices ||
        displayAttachmentsWithinMachine
      ) && (
        <MolsonTypography my={0} variant="h3">
          {t('noItemsSelected')} {itemType.toLocaleLowerCase()}
        </MolsonTypography>
      )}
    </MolsonBox>
  )
}
